import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { checkSession, getUser, startSession } from '../../utils/session';
import { Button, Field, Input } from '../../components/admin/ui';
import { IconAlert, IconLock, IconShield } from '../../components/admin/icons';
import SiteCredit from '../../components/SiteCredit';

/** Brand promise from the ParcelBuddy brand sheet. */
const promises = [
  'Right information, from the first message.',
  'Straight to the right people.',
  'Fewer lost parcels, happier customers.'
];

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already signed in? Skip the form.
  useEffect(() => {
    if (checkSession('admin') && getUser('admin')) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/api/auth/login', formData);
      startSession('admin', { token: response.data.token, user: response.data.user });
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* Brand panel — desktop only */}
      <div className="relative hidden w-[46%] max-w-2xl flex-col justify-between overflow-hidden bg-brand-900 p-12 lg:flex">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-700/40 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-gold-500/10 blur-3xl"
          aria-hidden="true"
        />

        {/* Just the coin here: the full wordmark lockup sits on the light side,
            so repeating it on the panel would show the logo twice. */}
        <Link
          to="/"
          className="relative flex w-fit items-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label="Back to ParcelBuddy"
        >
          <img src="/logo.png" alt="" className="h-12 w-12 shrink-0 rounded-full" />
        </Link>

        <div className="relative">
          <h2 className="font-display text-[34px] font-800 leading-[1.15] tracking-tight text-white">
            Delivery issues.
            <br />
            <span className="text-gold-400">Sorted.</span>
          </h2>
          <ul className="mt-8 space-y-4">
            {promises.map((promise) => (
              <li key={promise} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold-500/15 text-gold-400">
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="text-[15px] leading-relaxed text-brand-100/80">{promise}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-brand-100/60">
          Structured cases instead of delivery-issue emails.
        </p>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-[400px]">
          {/* The full wordmark lockup, on the light surface it was drawn for —
              same artwork the public site header uses. */}
          <div className="mb-8">
            <img src="/brand.png" alt="ParcelBuddy" className="h-16 w-auto" />
            <p className="mt-2 text-[11px] font-500 uppercase tracking-[0.16em] text-gold-600">
              Admin console
            </p>
          </div>

          <div className="rounded-2xl border border-ink-200 bg-white p-7 shadow-card sm:p-8">
            <div className="mb-6">
              <h1 className="font-display text-xl font-700 tracking-tight text-ink-900">Sign in</h1>
              <p className="mt-1.5 text-sm text-ink-500">
                Use your ParcelBuddy administrator account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-lg bg-danger-50 px-3.5 py-3 ring-1 ring-inset ring-danger-100"
                >
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-danger-600" />
                  <p className="text-sm text-danger-700">{error}</p>
                </div>
              )}

              <Field label="Email address" htmlFor="email" required>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="username"
                  placeholder="you@parcelbuddy.co.uk"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Field>

              <Field label="Password" htmlFor="password" required>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="pr-16"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-xs font-600 text-ink-500 transition-colors hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>

              <Button type="submit" size="lg" loading={loading} className="w-full">
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>

              <p className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
                >
                  Forgot your password?
                </Link>
              </p>
            </form>

            <div className="mt-6 flex items-start gap-2 border-t border-ink-100 pt-5 text-xs text-ink-500">
              <IconLock size={14} className="mt-px shrink-0 text-ink-500" />
              <p>Sessions end automatically after 24 hours of inactivity.</p>
            </div>
          </div>

          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-ink-500">
            <IconShield size={13} />
            Administrator access only
          </p>
          <SiteCredit className="mt-3 text-center" />
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
